export const BAD_ADDRESS_REASONS = {
  MISSING_PLACE: 'missing_place',
  WRONG_COUNTRY: 'wrong_country',
  MISSING_STREET_NUMBER: 'missing_street_number',
  IMPRECISE: 'imprecise',
};

// Types that describe an area or a whole street rather than a single address.
const IMPRECISE_TYPES = [
  'route',
  'intersection',
  'locality',
  'sublocality',
  'neighborhood',
  'postal_code',
  'administrative_area_level_1',
  'administrative_area_level_2',
  'administrative_area_level_3',
  'political',
  'plus_code',
];

const PRECISE_TYPES = [
  'street_address',
  'premise',
  'subpremise',
  'point_of_interest',
  'establishment',
];

const getTypes = place => (Array.isArray(place.types) ? place.types : []);

const getComponents = place => {
  const components = place.addressComponents || place.address_components;
  return Array.isArray(components) ? components : [];
};

const hasType = (place, type) => getTypes(place).includes(type);

const findComponent = (place, type) =>
  getComponents(place).find(c => Array.isArray(c.types) && c.types.includes(type));

const componentValue = (component, variant) => {
  if (!component) return '';
  if (variant === 'short') return component.shortText || component.short_name || '';
  return component.longText || component.long_name || '';
};

/**
 * @param {object} place - Place returned by Places Autocomplete / Place Details.
 * @param {string} [expectedCountryCode] - ISO code the address must belong to
 *   (e.g. 'GR', 'CY'). Skipped when not given.
 * @returns {string|null} one of BAD_ADDRESS_REASONS, or null when the place is usable.
 */
export const getBadAddressReason = (place, expectedCountryCode) => {
  if (!place) return BAD_ADDRESS_REASONS.MISSING_PLACE;

  const components = getComponents(place);
  if (components.length === 0) return BAD_ADDRESS_REASONS.MISSING_PLACE;

  const country = componentValue(findComponent(place, 'country'), 'short');
  if (
    expectedCountryCode &&
    country &&
    country.toUpperCase() !== expectedCountryCode.toUpperCase()
  ) {
    return BAD_ADDRESS_REASONS.WRONG_COUNTRY;
  }

  const types = getTypes(place);
  const isEstablishment = hasType(place, 'establishment') || hasType(place, 'point_of_interest');

  const isThinEstablishment = isEstablishment && components.length < 4;
  const isThinPlusCode = hasType(place, 'plus_code') && components.length < 4;
  if (isThinEstablishment || isThinPlusCode) return BAD_ADDRESS_REASONS.IMPRECISE;

  const hasRoute = !!componentValue(findComponent(place, 'route'), 'long');
  const hasStreetNumber = !!componentValue(findComponent(place, 'street_number'), 'long');

  if (hasRoute && !hasStreetNumber) return BAD_ADDRESS_REASONS.MISSING_STREET_NUMBER;

  if (!(hasRoute && hasStreetNumber) && types.length > 0) {
    const isImprecise =
      types.some(t => IMPRECISE_TYPES.includes(t)) && !types.some(t => PRECISE_TYPES.includes(t));
    if (isImprecise) return BAD_ADDRESS_REASONS.IMPRECISE;

    const hasKnownType = types.some(
      t => PRECISE_TYPES.includes(t) || IMPRECISE_TYPES.includes(t) || t === 'geocode',
    );
    if (!hasKnownType) return BAD_ADDRESS_REASONS.IMPRECISE;
  }

  if (!hasStreetNumber) return BAD_ADDRESS_REASONS.MISSING_STREET_NUMBER;

  return null;
};

export const badAddressCheck = (place, expectedCountryCode) =>
  getBadAddressReason(place, expectedCountryCode) !== null;
